import React, { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTab } from "../../../contexts/tab";
import { basePath } from "../../config";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/auth";
import { useFormik } from "formik";
import classNames from "classnames";
import * as Yup from "yup";

const submitFormData = async (
  values,
  actions,
  tabData,
  isEditMode,
  activeUser,
  upToEdit,
  setTabData,
  setIsOpen,
  setUpToEdit
) => {
  const current_page = tabData?.current_page;

  // TODO:: add form validation
  try {
    const res = await fetch(
      basePath + (isEditMode ? "editUp?page=" : "addUp?page=") + current_page,
      {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeUser?.remember_token}`,
        },
        body: JSON.stringify({ ...values, id: upToEdit?.id }, null, 2),
      }
    );

    const data = await res.json();

    if (res.status === 401) {
      toast.error(data.message);
      actions.resetForm({ values: values });
      document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return 0;
    }

    if (data.status) {
      console.log(JSON.parse(data.data));
      setTabData({ ...JSON.parse(data.data), links: tabData.links });
      setIsOpen(false);
      actions.resetForm({ ups: "", note: "" });
      setUpToEdit(null);
      toast.success(data.message);
    } else {
      // actions.resetForm({ values: values });
      toast.error("error: " + data.message);
      console.error("error: " + data.message);
    }
  } catch (error) {
    actions.resetForm({ values: values });
    toast.error("error: " + error);
    console.error("TabProvider:: api calling fail =>", error);
  }
};

const validationSchema = Yup.object().shape({
  ups: Yup.number().required(),
});

export default function UpForm({ setIsOpen, isOpen, upToEdit, setUpToEdit }) {
  const [{ tabData }, { setTabData }] = useTab();
  const [{ activeUser }] = useAuth();
  const isEditMode = !!upToEdit;

  useEffect(() => {
    if (upToEdit) {
      formik.setFieldValue("ups", upToEdit?.ups);
      formik.setFieldValue("note", upToEdit?.note || "");
    }
  }, [upToEdit]);

  function closeModal() {
    formik.resetForm({ ups: "", note: "" });
    setIsOpen(false);
    setUpToEdit(null);
  }

  const formik = useFormik({
    initialValues: { ups: "", note: "" },
    validationSchema,
    onSubmit: (values, actions) => {
      submitFormData(
        values,
        actions,
        tabData,
        isEditMode,
        activeUser,
        upToEdit,
        setTabData,
        setIsOpen,
        setUpToEdit
      );
    },
  });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={() => { }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {upToEdit ? "Edit" : " Add"} Up
                </Dialog.Title>
                <form
                  className="mt-2 grid gap-4"
                  onSubmit={formik.handleSubmit}
                >
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700 capitalize">
                      Up
                    </span>
                    <input
                      type="number"
                      name="ups"
                      step="0.001"
                      value={formik.values.ups}
                      onChange={formik.handleChange}
                      className={classNames(
                        "mt-1 block w-full px-3 py-2 bg-white border  rounded-md text-sm shadow-sm placeholder-slate-400 disabled:bg-slate-300",
                        formik.errors?.ups && "border-red-600"
                      )}
                    />
                  </label>
                  {formik.errors.ups ? (
                    <span className="text-sm text-red-600">
                      {formik.errors?.ups}
                    </span>
                  ) : null}

                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700 capitalize">
                      notes
                    </span>
                    <textarea
                      name="note"
                      value={formik.values.note}
                      onChange={formik.handleChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm shadow-sm placeholder-slate-400"
                      rows="5"
                    ></textarea>
                  </label>
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formik.isSubmitting}
                      className={classNames(
                        "inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
                        formik.isSubmitting && "disabled:bg-slate-300"
                      )}
                    >
                      {upToEdit ? "Edit" : " Add"} Ups
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
